import {useEffect, useState} from 'react';
import {from} from 'rxjs';
import {last, mergeAll, tap} from 'rxjs/operators';
import useDevices from 'src/hooks/use-devices';
import useSocket from 'src/hooks/use-socket';
import {useFirstMount} from 'src/hooks/use-first-mount.js';
import {actions} from '../reducers.js';
import useReportActive from './useReportActive';

export const applyDevicesRemoval = (map, deviceIds) => {
  if (!Array.isArray(deviceIds)) return;
  deviceIds.forEach((id) => map.removeDeviceFeature(id));
};

// deviceMap nasce null e so e populado depois, por um efeito assincrono separado
// (useMap.js). Sem essa checagem, um update de device vindo do socket antes do
// deviceMap estar pronto derruba o assinante com "can't access property get, i is null".
export const shouldSubscribeToDeviceUpdates = (map, deviceMap, deviceStore) =>
  Boolean(map && deviceMap && deviceStore.list.length);

/**
 * Diz se o efeito de visibilidade da camada de dispositivos já pode chamar o mapa.
 *
 * CONSTRAINT: o mapa é `null` até o efeito de criação rodar (`useMap.js`), então o efeito de visibilidade
 * espera por ele. Fora da primeira renderização aplica sempre; na primeira, só com relatório ativo (link
 * direto com `#route`), quando a camada precisa nascer oculta.
 *
 * @param {object|null} map instância do mapa, `null` antes da criação
 * @param {boolean} isFirstMount se é a primeira renderização do hook
 * @param {boolean} reportActive se há um relatório aberto no mapa
 * @return {boolean} `true` quando a visibilidade pode ser aplicada
 */
export const shouldApplyDevicesLayerVisibility = (map, isFirstMount, reportActive) =>
  Boolean(map && (!isFirstMount || reportActive));

const useMapDevice = (state, dispatch, instance) => {
  const {instance: map, deviceMap} = state;
  const {initAllDevices, cluster, centerOnCluster} = instance.current.props; // ref. i.e. not reactive

  const isFirstMount = useFirstMount();
  const [isDevicesLayerVisible, setDevicesLayerVisible] = useState(true);
  const reportActive = useReportActive();

  const {devices: devicesObservable, devicesRemoved: devicesRemovedObservable} = useSocket();
  const {devices: deviceStore} = useDevices();

  const toggleDevicesLayerVisibility = () => {
    setDevicesLayerVisible((prev) => !prev);
  };

  useEffect(() => {
    if (map) {
      map.initDevicesLayer();
    }

    return () => {
      if (map) {
        map.removeDevices();
      }
    };
  }, [map]);

  useEffect(() => {
    // reportActive também entra na primeira renderização (link direto para o relatório, hash já
    // com #route): sem isso o mapa mostraria os dispositivos até o próximo toggle manual.
    if (shouldApplyDevicesLayerVisibility(map, isFirstMount, reportActive)) {
      map.setLayerVisibilityByLayerName('devicesLayer', isDevicesLayerVisible && !reportActive);
    }
  }, [map, isDevicesLayerVisible, reportActive]);

  useEffect(() => {
    let devicesStreamSubscription$;

    if (map && deviceMap && deviceStore.updatedPositions) {
      devicesStreamSubscription$ = from(deviceStore.list)
          .pipe(
              tap((device) => {
                if (initAllDevices) {
                  map.addFeatureDevice(device);
                }
              }),
              last(),
          )
          .subscribe((_) => {
            const clusterDistance = cluster && cluster.distance;

            dispatch({
              type: actions.SET_INITIALIZED_POSITIONS,
              payload: deviceStore.updatedPositions ? deviceStore.updatedPositions : false,
            });

            if (clusterDistance) map.defineDeviceCluster(clusterDistance, centerOnCluster);
          });
    }

    return () => {
      if (devicesStreamSubscription$) {
        devicesStreamSubscription$.unsubscribe();
      }
    };
  }, [
    map,
    deviceMap,
    deviceStore.updatedPositions,
    initAllDevices,
    cluster,
    centerOnCluster,
  ]);

  useEffect(() => {
    let devicesSubscription$;

    if (shouldSubscribeToDeviceUpdates(map, deviceMap, deviceStore)) {
      devicesSubscription$ = devicesObservable
        .pipe(
          mergeAll(),
        )
        .subscribe((device) => {
          const _device = deviceMap.get(+device.id);

          if (_device) {
            map.updateDevice(_device);
            if (_device.disabled) {
              map.handleDeviceDisabled(_device.id);
            } else {
              map.handleDeviceEnabled(_device.id);
            }
          }
        });
      }

      return () => {
        if (devicesSubscription$) {
          devicesSubscription$.unsubscribe();
        }
      };
  }, [map, deviceStore.list, deviceMap]);

  useEffect(() => {
    let devicesRemovedSubscription$;

    if (map) {
      devicesRemovedSubscription$ = devicesRemovedObservable.subscribe((deviceIds) => {
        applyDevicesRemoval(map, deviceIds);
      });
    }

    return () => {
      if (devicesRemovedSubscription$) {
        devicesRemovedSubscription$.unsubscribe();
      }
    };
  }, [map, devicesRemovedObservable]);

  return {
    showDevices: isDevicesLayerVisible,
    setShowDevices: toggleDevicesLayerVisibility,
  };
};

export default useMapDevice;
